/*
 * Copyright 2022-present terrestris GmbH & Co. KG
 *
 * Redistribution and use in source and binary forms, with or without modification,
 * are permitted provided that the following conditions are met:
 *
 * 1. Redistributions of source code must retain the above copyright notice, this
 * list of conditions and the following disclaimer.
 *
 * 2. Redistributions in binary form must reproduce the above copyright notice,
 * this list of conditions and the following disclaimer in the documentation
 * and/or other materials provided with the distribution.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND
 * ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED
 * WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED.
 * IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT,
 * INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE,
 * DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF
 * LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE
 * OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED
 * OF THE POSSIBILITY OF SUCH DAMAGE.
 *
 */
import React, {
  useCallback,
  useEffect
} from 'react';

import OlLayerBase from 'ol/layer/Base';
import OlLayerImage from 'ol/layer/Image';
import OlLayerTile from 'ol/layer/Tile';
import OlSourceImageWMS from 'ol/source/ImageWMS';
import OlSourceTileWMS from 'ol/source/TileWMS';

import {
  useTranslation
} from 'react-i18next';

import {
  CoordinateInfo,
  CoordinateInfoProps
} from '@terrestris/react-geo/dist/CoordinateInfo/CoordinateInfo';
import {
  useMap
} from '@terrestris/react-util';

import { CoordinateInfoResult } from '@terrestris/react-util/dist/Hooks/useCoordinateInfo/useCoordinateInfo';

import {
  useAppDispatch
} from '../../../hooks/useAppDispatch';
import {
  useAppSelector
} from '../../../hooks/useAppSelector';
import {
  open
} from '../../../store/drawer';

import FeaturePropertyGrid from '../FeaturePropertyGrid';

import './index.less';

export type FeatureInfoProps = {
  enabled?: boolean;
} & Partial<CoordinateInfoProps>;

export const FeatureInfo: React.FC<FeatureInfoProps> = ({
  enabled,
  ...restProps
}): React.JSX.Element => {
  const {
    t
  } = useTranslation();

  const map = useMap();
  const dispatch = useAppDispatch();
  const featureInfoActive = useAppSelector((state) => state.featureInfo.active);

  if (!map) {
    return <></>;
  }

  const showDrawer = useCallback(() => {
    if (!featureInfoActive) { return; }
    dispatch(open());
  }, [dispatch, featureInfoActive]);

  useEffect(() => {
    const handler = () => showDrawer();
    map.on('click', handler);
    return () => {
      map.un('click', handler);
    };
  }, [map, featureInfoActive, showDrawer]);

  const layerFilter = (layer: OlLayerBase) => {
    if (!layer.get('hoverable')) {
      return false;
    }

    if (layer instanceof OlLayerImage && layer.getSource() instanceof OlSourceImageWMS) {
      return true;
    }

    if (layer instanceof OlLayerTile && layer.getSource() instanceof OlSourceTileWMS) {
      return true;
    }

    return false;
  };

  const resultRenderer = (coordinateInfoState: CoordinateInfoResult) => {
    const featureLayerResults = coordinateInfoState.features;
    const loading = coordinateInfoState.loading;

    if (Object.keys(featureLayerResults).length === 0) {
      return (
        <span className='usage-hint'>
          {t('FeatureInfo.usageHint')}
        </span>
      );
    }

    return featureLayerResults
      .map((flr): React.JSX.Element => {
        return (
          <div key={flr.feature.getId()}>
            <FeaturePropertyGrid
              features={[flr.feature]}
              layerName={flr.featureType}
              loading={loading}
            />
          </div>
        );
      });
  };

  if (!enabled) {
    return <></>;
  }

  return (
    <div className='feature-info-panel'>
      <CoordinateInfo
        active={enabled}
        featureCount={10}
        layerFilter={layerFilter}
        resultRenderer={resultRenderer}
        {...restProps}
      />
    </div>
  );
};

export default FeatureInfo;
